import { useState } from "react";
import { subscribeEmail } from "@/lib/newsletter.functions";
import { track } from "@/lib/analytics";
import { validateEmail } from "@/lib/email-validation";
import { trackMarketingSignupSuccess } from "@/lib/marketing/analytics";

export function NewsletterForm({ source }: { source: string }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "success" | "duplicate" | "error">("idle");
  const [message, setMessage] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === "loading") return;
    const check = validateEmail(email);
    if (!check.ok) {
      setState("error");
      setMessage(check.error ?? "Enter a valid email address.");
      return;
    }
    setState("loading");
    setMessage("");
    try {
      const res = await subscribeEmail({ data: { email, source } });
      track("newsletter_submit", { source, duplicate: Boolean(res.duplicate) });
      trackMarketingSignupSuccess({ source, duplicate: Boolean(res.duplicate) });
      if (res.duplicate) {
        setState("duplicate");
        setMessage(
          res.status === "confirmed"
            ? "You're already in the field."
            : "You're already on the list — check your inbox for the confirmation.",
        );
      } else {
        setState("success");
        setMessage("Check your inbox to confirm your subscription.");
        setEmail("");
      }
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error && err.message ? err.message : "Please enter a valid email.");
    }
  }

  return (
    <>
      <form onSubmit={onSubmit} className="flex flex-col gap-2 sm:flex-row" noValidate>
        <label htmlFor={`newsletter-${source}`} className="sr-only">Email</label>
        <input
          id={`newsletter-${source}`}
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@sacred.address"
          disabled={state === "loading"}
          aria-invalid={state === "error"}
          aria-describedby={message ? `newsletter-${source}-msg` : undefined}
          className="min-w-0 flex-1 rounded-full border hairline bg-obsidian px-4 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-gold disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={state === "loading"}
          className="shrink-0 rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian disabled:opacity-60"
        >
          {state === "loading" ? "Sending…" : "Subscribe"}
        </button>
      </form>
      {message && (
        <p
          id={`newsletter-${source}-msg`}
          role={state === "error" ? "alert" : "status"}
          aria-live="polite"
          className={`mt-2 text-xs ${state === "error" ? "text-red-300" : "text-gold-luminous"}`}
        >
          {message}
        </p>
      )}
    </>
  );
}
